
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../services/supabaseClient';
import { Region, TherapeuticArea, RegulatoryPathway, ServiceType, ProductType, CompanySize } from '../../types';
import { Check, ArrowRight, ArrowLeft, Building2, Globe, Award, Users, DollarSign, AlertCircle } from 'lucide-react';
import { CheckoutModal } from '../shared/CheckoutModal';

export const ConsultancyOnboarding: React.FC = () => {
  const { user, completeOnboarding } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const totalSteps = 6; 
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    size: CompanySize.BOUTIQUE,
    description: '',
    regions: [] as Region[],
    therapeuticAreas: [] as TherapeuticArea[],
    pathways: [] as RegulatoryPathway[],
    services: [] as ServiceType[],
    productTypes: [] as ProductType[],
    approvals: 0,
    designations: 0,
    teamSize: 1,
    rateStart: '',
    rateEnd: '',
    tier: 'foundation'
  });

  // Pre-fill plan
  useEffect(() => {
      if (location.state?.plan) {
          setFormData(prev => ({ ...prev, tier: location.state.plan }));
      }
  }, [location.state]);

  const handleNext = async () => {
    setError('');
    // Check for payment on step 5
    if (step === 5) {
      if (!paymentComplete && formData.tier !== 'foundation') {
        setShowCheckout(true);
        return;
      }
    }

    if (step < totalSteps) {
      setStep(step + 1);
    } else {
        // Final Submission
        if (!user) {
          setError('Authentication lost. Please login again.');
          return;
        }
        setIsSaving(true);

        try {
          // Insert into Supabase
          const { error: dbError } = await supabase.from('consultancies').insert({
            owner_id: user.id,
            name: formData.name,
            website_url: formData.website,
            company_size: formData.size,
            description: formData.description,
            regions: formData.regions,
            therapeutic_areas: formData.therapeuticAreas,
            pathways: formData.pathways,
            services: formData.services,
            product_types: formData.productTypes,
            verified_approvals_count: formData.approvals,
            orphan_designations_count: formData.designations,
            team_size: formData.teamSize,
            hourly_rate_start: parseInt(formData.rateStart) || 0,
            hourly_rate_end: parseInt(formData.rateEnd) || 0,
            subscription_plan: formData.tier,
            slug: formData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + Math.floor(Math.random() * 1000),
            status: 'pending' // IMPORTANT: pending admin review
          });

          if (dbError) throw dbError;

          await completeOnboarding();
          navigate('/dashboard'); 
        } catch (err: any) {
          console.error('Onboarding Error:', err);
          setError(err.message || 'Failed to save profile. Please try again.');
          setIsSaving(false);
        }
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const toggleSelection = <T,>(item: T, current: T[], field: string) => {
    const newSelection = current.includes(item) 
        ? current.filter(i => i !== item)
        : [...current, item];
    setFormData({ ...formData, [field]: newSelection });
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
       <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <div className="flex justify-between text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">
                <span>Company Info</span>
                <span>Expertise</span>
                <span>Track Record</span>
                <span>Pricing & Team</span>
                <span>Plan</span>
                <span>Submit</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                    className="h-full bg-blue-600 transition-all duration-500 ease-out"
                    style={{ width: `${(step / totalSteps) * 100}%` }}
                />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
             {error && (
               <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded mb-6 flex items-center gap-2">
                 <AlertCircle className="w-5 h-5" />
                 {error}
               </div>
             )}
             
             {/* Step 1 to 5 rendered here (omitted mostly for brevity as they are static form fields, focus is on logic) */}
             {step === 1 && (
                <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-slate-900">Tell us about your consultancy</h2>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Consultancy Name</label>
                        <input 
                            type="text" 
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                            className="w-full p-3 border border-slate-300 rounded-lg focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Website</label>
                        <input 
                            type="url" 
                            value={formData.website}
                            onChange={(e) => setFormData({...formData, website: e.target.value})}
                            className="w-full p-3 border border-slate-300 rounded-lg focus:ring-blue-500"
                        />
                    </div>
                     <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                        <textarea 
                            value={formData.description}
                            onChange={(e) => setFormData({...formData, description: e.target.value})}
                            className="w-full p-3 border border-slate-300 rounded-lg focus:ring-blue-500"
                            rows={4}
                        />
                    </div>
                </div>
             )}

             {step === 2 && (
                 <div className="space-y-6">
                     <h2 className="text-2xl font-bold text-slate-900">Areas of Expertise</h2>
                     <p className="text-slate-500">Select all relevant Health Authorities.</p>
                     <div className="grid grid-cols-2 gap-2">
                        {Object.values(Region).map(r => (
                             <button key={r} onClick={() => toggleSelection(r, formData.regions, 'regions')} className={`p-2 border rounded ${formData.regions.includes(r) ? 'bg-blue-50 border-blue-500' : ''}`}>{r}</button>
                        ))}
                     </div>
                 </div>
             )}
             
             {step === 3 && (
                 <div className="space-y-6">
                     <h2 className="text-2xl font-bold text-slate-900">Track Record</h2>
                     <div>
                         <label>Approvals</label>
                         <input type="number" value={formData.approvals} onChange={e => setFormData({...formData, approvals: parseInt(e.target.value)})} className="w-full border p-2 rounded"/>
                     </div>
                 </div>
             )}

             {step === 4 && (
                 <div className="space-y-6">
                     <h2 className="text-2xl font-bold text-slate-900">Team & Pricing</h2>
                     <div className="grid grid-cols-2 gap-4">
                         <div>
                             <label>Hourly Rate (Min)</label>
                             <input type="number" value={formData.rateStart} onChange={e => setFormData({...formData, rateStart: e.target.value})} className="w-full border p-2 rounded"/>
                         </div>
                          <div>
                             <label>Hourly Rate (Max)</label>
                             <input type="number" value={formData.rateEnd} onChange={e => setFormData({...formData, rateEnd: e.target.value})} className="w-full border p-2 rounded"/>
                         </div>
                     </div>
                 </div>
             )}

             {step === 5 && (
                <div className="space-y-8">
                    <div className="text-center mb-8">
                        <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                            <DollarSign className="w-6 h-6 text-blue-700"/>
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900">Choose your plan</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {['Foundation', 'Professional', 'Enterprise'].map((tierName) => (
                            <div 
                                key={tierName}
                                onClick={() => setFormData({...formData, tier: tierName.toLowerCase()})}
                                className={`border-2 rounded-xl p-6 cursor-pointer transition-all ${formData.tier === tierName.toLowerCase() ? 'border-blue-600 bg-blue-50' : 'border-slate-200'}`}
                            >
                                <h3 className="font-bold text-slate-900">{tierName}</h3>
                            </div>
                        ))}
                    </div>
                    {paymentComplete && <div className="text-green-600 font-bold text-center">Payment Verified ✓</div>}
                </div>
             )}
             
             {step === 6 && (
                 <div className="text-center py-8">
                     <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                         <Check className="w-8 h-8 text-green-600" />
                     </div>
                     <h2 className="text-2xl font-bold text-slate-900 mb-4">Ready to Submit</h2>
                     <p className="text-slate-600 mb-8 max-w-md mx-auto">
                         Your profile will be reviewed by our admin team.
                     </p>
                 </div>
             )}

             <div className="mt-10 flex justify-between pt-6 border-t border-slate-100">
                 <button onClick={handleBack} disabled={step === 1 || isSaving} className="flex items-center px-6 py-3 text-slate-600">
                     <ArrowLeft className="w-4 h-4 mr-2" /> Back
                 </button>
                 
                 <button 
                    onClick={handleNext}
                    disabled={isSaving}
                    className="flex items-center px-8 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-800"
                 >
                    {step === 5 && !paymentComplete ? 'Proceed to Payment' : (step === 6 ? (isSaving ? 'Submitting...' : 'Submit') : 'Continue')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                 </button>
             </div>
          </div>
       </div>
       
       {showCheckout && (
           <CheckoutModal 
               isOpen={showCheckout} 
               onClose={() => {
                   setShowCheckout(false);
                   setPaymentComplete(true);
                   setStep(6);
               }} 
            />
       )}
    </div>
  );
};
